import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Search, User, ShoppingBag, ChevronDown } from "lucide-react";
import { brandAssets } from "../config/media";

const VERSIONS = [
  { label: "V1", href: "/" },
  { label: "V1.2", href: "/v1.2" },
  { label: "V1.3", href: "/v1.3" },
];

const NAV = [
  { label: "Strollee S1", href: "#s1", testid: "header-nav-s1" },
  { label: "Ways to Stroll", href: "#ways-to-stroll", testid: "header-nav-ways-to-stroll" },
  { label: "How It Works", href: "#how-it-works", testid: "header-nav-how-it-works" },
  { label: "Accessories", href: "#accessories", testid: "header-nav-accessories" },
  { label: "Reviews", href: "#reviews", testid: "header-nav-reviews" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const { pathname } = useLocation();
  const currentVersion = VERSIONS.find((v) => v.href === pathname)?.label ?? "V1";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`sticky top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ease-in-out ${
        scrolled
          ? "bg-[#FCFBF8]/85 backdrop-blur-md border-b border-[#D9D6CE]/60 shadow-[0_1px_12px_rgba(23,23,19,0.04)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="px-12 lg:px-16 h-[76px] lg:h-20 flex items-center justify-between gap-8">
        <a
          href="/"
          data-testid="header-logo-link"
          className="flex items-center leading-none select-none"
        >
          <img
            src={brandAssets.logoDark.url}
            alt={brandAssets.logoDark.alt}
            className="h-[22px] w-auto"
            draggable={false}
          />
        </a>

        <nav aria-label="Primary" className="flex items-center gap-9">
          {NAV.map((item) => (
            <a
              key={item.label}
              href={item.href}
              data-testid={item.testid}
              className="group relative text-sm font-medium text-[var(--strollee-ink)]/80 transition-colors duration-200 hover:text-[var(--strollee-ink)]"
            >
              {item.label}
              <span className="absolute -bottom-1.5 left-0 h-[1.5px] w-full origin-left scale-x-0 bg-[var(--strollee-accent)] transition-transform duration-300 ease-out group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <a
            href="#support"
            data-testid="header-utility-support"
            className="text-sm font-medium text-[var(--strollee-ink)]/80 transition-colors duration-200 hover:text-[var(--strollee-ink)]"
          >
            Support
          </a>
          <div className="flex items-center gap-4">
            <button
              type="button"
              aria-label="Search"
              data-testid="header-utility-search"
              className="text-[var(--strollee-ink)]/80 transition-all duration-200 hover:text-[var(--strollee-ink)] hover:-translate-y-[1px]"
            >
              <Search size={19} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              aria-label="Account"
              data-testid="header-utility-account"
              className="text-[var(--strollee-ink)]/80 transition-all duration-200 hover:text-[var(--strollee-ink)] hover:-translate-y-[1px]"
            >
              <User size={19} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              aria-label="Bag"
              data-testid="header-utility-bag"
              className="relative text-[var(--strollee-ink)]/80 transition-all duration-200 hover:text-[var(--strollee-ink)] hover:-translate-y-[1px]"
            >
              <ShoppingBag size={19} strokeWidth={1.8} />
              <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--strollee-ink)] text-[9px] font-semibold text-[var(--strollee-surface)]">
                0
              </span>
            </button>
          </div>
          <div
            className="relative"
            onMouseEnter={() => setVersionOpen(true)}
            onMouseLeave={() => setVersionOpen(false)}
          >
            <button
              type="button"
              data-testid="header-version-dropdown"
              className="flex items-center gap-1 text-[11px] font-medium tracking-wide text-[var(--strollee-muted)]/50 transition-colors duration-200 hover:text-[var(--strollee-ink)]"
            >
              {currentVersion}
              <ChevronDown
                size={11}
                strokeWidth={2}
                className={`transition-transform duration-200 ${versionOpen ? "rotate-180" : ""}`}
              />
            </button>
            {versionOpen && (
              <div className="absolute right-0 top-full pt-2">
                <div className="overflow-hidden rounded-xl border border-[var(--strollee-line)] bg-[var(--strollee-surface)] py-1 shadow-[0_8px_24px_rgba(23,23,19,0.10)]">
                  {VERSIONS.map((v) => (
                    <a
                      key={v.label}
                      href={v.href}
                      data-testid={`header-version-option-${v.label.toLowerCase().replace(".", "")}`}
                      className={`block px-4 py-2 text-[11px] font-medium tracking-wide transition-colors duration-150 ${
                        v.label === currentVersion
                          ? "text-[var(--strollee-ink)]"
                          : "text-[var(--strollee-muted)]/60 hover:text-[var(--strollee-ink)]"
                      }`}
                    >
                      {v.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <a
            href="#build"
            data-testid="header-build-strollee-cta"
            className="rounded-full bg-[var(--strollee-ink)] px-5 py-2.5 text-[13px] font-semibold tracking-wide text-[var(--strollee-surface)] transition-all duration-200 hover:bg-[var(--strollee-accent-deep)] hover:shadow-lg hover:-translate-y-[1px]"
          >
            Build my Strollee
          </a>
        </div>
      </div>
    </motion.header>
  );
};
